import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore, type AuthStore } from '@/store/authStore';
import {
  getNavigation,
  getNavigationBadges,
  type AccountBranding,
  type NavItem,
} from '@/services/navigation.service';
import { getInboxCounts } from '@/services/communications.service';
import { cn, hexToHslString, applyBrandingColors } from '@/lib/utils';
import { endSession } from '@/lib/session';
import { CollapseChevron } from '@/components/ui/collapse-chevron';
import { UserPillMenu } from '@/components/layout/UserPillMenu';
import { SidebarPatientSearch } from '@/components/layout/SidebarPatientSearch';
import {
  logoSubTextColor,
  logoSubTextFontSize,
  logoSubTextFontStack,
  logoSubTextLetterSpacing,
  logoSubTextOffsetY,
} from '@/v5/lib/logoSubTextFonts';
import { useImageLoadState } from '@/v5/hooks/useImageLoadState';
import {
  Sidebar as SidebarPrimitive,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader, 
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSkeleton,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from '@/components/ui/sidebar';
import { type NavItemDef } from '@/components/layout/navigation.config';
import { useFilteredNavItems } from '@/hooks/useNavAccess';
import { COMM_CENTER_NAV_HREF, groupBadgesFor, leafBadgeFor, MENTIONS_NAV_HREF } from '@/components/layout/navBadges';
import { MentionsPreview } from '@/components/layout/MentionsPreview';
import { facilityScopeKey } from '@/lib/facilityScopeParams';
import { labsNavHref } from '@/pages/lab-orders/labOrdersFilters';
import { useCloseMobileNav } from '@/hooks/useCloseMobileNav';
import { useMyTaskCounts } from '@/hooks/useMyTaskCounts';
import { useUnreadAlertCount } from '@/hooks/useUnreadAlertCount';
import {
  ChevronDown,
  ChevronRight,
  HeartPulse,
  LogOut,
  ExternalLink,
} from 'lucide-react';
import { GlobalFacilitySelector } from '@/v5/components/GlobalFacilitySelector';
import { useGlobalFacilityStore } from '@/store/globalFacilityStore';

// ─── Dark sidebar CSS variables ──────────────────────────────────────────────

const DARK_SIDEBAR_VARS = {
  '--sidebar': '240 10% 14%',
  '--sidebar-foreground': '0 0% 100%',
  '--sidebar-primary': '217 91% 60%',
  '--sidebar-primary-foreground': '0 0% 100%',
  '--sidebar-accent': '0 0% 100% / 0.1',
  '--sidebar-accent-foreground': '0 0% 100%',
  '--sidebar-border': '0 0% 100% / 0.1',
  '--sidebar-ring': '217 91% 60%',
} as React.CSSProperties;

// ─── Helpers ─────────────────────────────────────────────────────────────────

function isDescendantActive(item: NavItemDef, pathname: string, search: string): boolean {
  if (item.href) {
    const url = new URL(item.href, 'http://x');
    if (url.pathname === pathname && url.search === search) return true;
    if (!url.search && pathname.startsWith(url.pathname) && url.pathname !== '/') return true;
  }
  return item.children?.some((c) => isDescendantActive(c, pathname, search)) ?? false;
}

/**
 * Whether a sub-row is the page being viewed. As with a top-level leaf, a row
 * configured without a query string owns its whole pathname, so it stays lit
 * while the page keeps its own tab in the URL (Templates on ?tab=sms) — unless
 * a sibling was configured for that exact query, which then takes the highlight
 * alone (Inquiries, not Plans, on /plans?view=inquiries).
 */
function isChildActive(
  href: string,
  siblings: NavItemDef[],
  location: { pathname: string; search: string },
): boolean {
  const url = new URL(href, 'http://x');
  if (url.pathname !== location.pathname) return false;
  if (url.search) return url.search === location.search;
  return !siblings.some((s) => {
    if (!s.href) return false;
    const sibling = new URL(s.href, 'http://x');
    return (
      sibling.search !== '' &&
      sibling.pathname === location.pathname &&
      sibling.search === location.search
    );
  });
}

// ─── Inline count pill ───────────────────────────────────────────────────────
// The sidebar's one count badge — leaf rows, parent groups and sub-rows all use
// it, so a count sits immediately right of its label at every level. It replaces
// SidebarMenuBadge here: that primitive positions itself with `absolute right-1`
// and `peer-*` selectors that require it to be a following sibling of the menu
// button, so it can only ever pin to the row's right edge.
// `shrink-0` keeps it from compressing beside a truncating label, and the
// collapsed-rail hide is the one SidebarMenuBadge used to carry.
// Owns the empty/overflow guards so callers never repeat them.
export function NavBadge({ count }: { count?: number | null }) {
  if (count == null || count <= 0) return null;
  return (
    <span className="inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-semibold tabular-nums text-white group-data-[collapsible=icon]:hidden">
      {formatNavCount(count)}
    </span>
  );
}

function formatNavCount(count: number): string {
  return count > 99 ? '99+' : String(count);
}

// ─── Collapsed-rail count ────────────────────────────────────────────────────
// Teams keep the sidebar collapsed, so the count has to survive there. In icon
// mode the menu button is a clipped 32px square (`!size-8 overflow-hidden`), so
// the visible pill cannot live inside it: it is a sibling of the button,
// positioned on the menu item (already `relative`) over the icon's top-right
// corner. It renders only when the desktop rail is collapsed, never in the
// phone drawer; Sidebar.test.tsx covers both, including the transitions.
//
// The pill is hidden from assistive tech; NavRailCountLabel puts the same
// number inside the button so its accessible name matches the expanded row's.
// The ring uses the sidebar's own (brandable) background to separate the pill
// from the icon.
export function NavRailBadge({ count }: { count?: number | null }) {
  if (count == null || count <= 0) return null;
  return (
    <span
      data-rail-badge=""
      aria-hidden="true"
      className="pointer-events-none absolute left-[18px] top-0.5 z-10 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-semibold leading-none tabular-nums text-white ring-2 ring-sidebar"
    >
      {formatNavCount(count)}
    </span>
  );
}

function NavRailCountLabel({ count }: { count?: number | null }) {
  if (count == null || count <= 0) return null;
  return <span className="sr-only">{formatNavCount(count)}</span>;
}

// Recursive renderer for the collapsed-state popover. Supports a nested
// child group (e.g. Marketing → Analytics → Ads Dashboard).
function renderPopoverChildren(
  children: NavItemDef[],
  location: { pathname: string; search: string },
  navigate: (path: string) => void,
  setPopoverOpen: (open: boolean) => void,
  closeMobileNav: () => void,
  childBadges?: Record<string, number>,
): React.ReactNode {
  return children.map((child) => {
    if (child.children) {
      return (
        <div key={child.label} className="py-1">
          <p className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-white/40">{child.label}</p>
          {renderPopoverChildren(child.children, location, navigate, setPopoverOpen, closeMobileNav, childBadges)}
        </div>
      );
    }
    const isActive = isChildActive(child.href!, children, location);
    return (
      <button
        key={child.href}
        onClick={() => { closeMobileNav(); navigate(child.href!); setPopoverOpen(false); }}
        className={cn(
          'flex w-full items-center gap-2 rounded px-3 py-1.5 text-sm transition-colors',
          isActive
            ? 'bg-white/20 text-white font-medium'
            : 'text-white/70 hover:bg-white/10 hover:text-white',
        )}
      >
        <child.icon className="h-3.5 w-3.5 flex-shrink-0" />
        {child.label}
        {/* The menu portals to document.body, outside the sidebar's `group`
            wrapper, so NavBadge's icon-mode hide does not apply here. */}
        <NavBadge count={childBadges?.[child.href!]} />
      </button>
    );
  });
}

// ─── Section nav group (has href + children) ─────────────────────────────────
// Clicking the parent navigates to its dashboard AND opens the submenu. On a
// phone that tap deliberately leaves the drawer open: closing it there would put
// the submenu it just revealed out of reach. Only the leaf children below close.

export function NavGroupItem({
  item,
  childBadges,
  badge,
  childAddons,
}: {
  item: NavItemDef;
  childBadges?: Record<string, number>;
  badge?: number;
  /**
   * Extra sub-rows rendered straight after the child with this href (e.g. the
   * mentioned conversations under Mentions). Expanded sidebar only: the icon
   * rail's flyout lists the children alone.
   */
  childAddons?: Record<string, React.ReactNode>;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const closeMobileNav = useCloseMobileNav();
  const { state: sidebarState, isMobile } = useSidebar();
  // `state` derives from the persisted *desktop* open flag and never from `openMobile`,
  // so without the `isMobile` term a collapsed rail leaks into the mobile drawer. That
  // is reachable: a phone in landscape is >=768px and renders the desktop rail with its
  // collapse chevron, so collapsing there and rotating back gives the drawer the
  // icon-rail branch — whose popovers portal to `document.body`, outside the sheet and
  // therefore pointer-dead while it is open, while the `group-data-[collapsible=icon]`
  // classes do not apply so the labels still show. A half-collapsed state that exists
  // nowhere in the design. The primitive itself already guards this way (`sidebar.tsx`
  // hides the rail's own rail handle on `state !== 'collapsed' || isMobile`).
  const isCollapsed = sidebarState === 'collapsed' && !isMobile;
  const childActive = isDescendantActive(item, location.pathname, location.search);
  const [open, setOpen] = React.useState(childActive);
  const [popoverOpen, setPopoverOpen] = React.useState(false);
  const triggerRef = React.useRef<HTMLDivElement>(null);
  const popoverRef = React.useRef<HTMLDivElement>(null);
  const [popoverPos, setPopoverPos] = React.useState({ top: 0, left: 0 });
  const Icon = item.icon;

  // Keep submenu open whenever we're on a child route
  React.useEffect(() => {
    if (childActive) setOpen(true);
  }, [location.pathname, location.search, childActive]);

  // Close popover on outside click
  React.useEffect(() => {
    if (!popoverOpen) return;
    function handler(e: MouseEvent) {
      if (
        popoverRef.current && !popoverRef.current.contains(e.target as Node) &&
        triggerRef.current && !triggerRef.current.contains(e.target as Node)
      ) {
        setPopoverOpen(false);
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [popoverOpen]);

  React.useEffect(() => {
    if (!isCollapsed) setPopoverOpen(false);
  }, [isCollapsed]);

  // ── Collapsed: icon click navigates + shows children popover ─────────────
  if (isCollapsed) {
    return (
      <SidebarMenuItem>
        <div ref={triggerRef}>
          <SidebarMenuButton
            isActive={childActive}
            tooltip={item.label}
            onClick={() => {
              navigate(item.href!);
              if (triggerRef.current) {
                const rect = triggerRef.current.getBoundingClientRect();
                setPopoverPos({ top: rect.top, left: rect.right + 4 });
              }
              setPopoverOpen((o) => !o);
            }}
          >
            <Icon />
            <span>{item.label}</span>
            <NavRailCountLabel count={badge} />
          </SidebarMenuButton>
        </div>
        <NavRailBadge count={badge} />
        {popoverOpen && item.children && createPortal(
          <div
            ref={popoverRef}
            className="fixed z-50 min-w-[180px] max-h-[70vh] overflow-y-auto rounded-md border border-slate-700 bg-[hsl(222,47%,16%)] p-1.5 shadow-xl"
            style={{ top: popoverPos.top, left: popoverPos.left }}
          >
            <p className="px-3 py-1.5 text-xs font-semibold text-white/60">{item.label}</p>
            {renderPopoverChildren(item.children, location, navigate, setPopoverOpen, closeMobileNav, childBadges)}
          </div>,
          document.body,
        )}
      </SidebarMenuItem>
    );
  }

  // ── Expanded: click parent → navigate + toggle submenu ───────────────────
  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        isActive={childActive}
        tooltip={item.label}
        onClick={() => {
          navigate(item.href!);
          setOpen((o) => !o);
        }}
      >
        <Icon />
        {/* `truncate` so the label, badge and chevron stay on one line: the
            chevron is the last child, so the button's `[&>span:last-child]`
            truncation never reaches this span. */}
        <span className="truncate">{item.label}</span>
        {/* The badge sits with the label; only the chevron carries `ml-auto`.
            That keeps every group's chevron on the same vertical line whether
            or not the row has a count — a badge in the right-aligned group
            would push its chevron out of line with the others. */}
        <NavBadge count={badge} />
        {open
          ? <ChevronDown className="ml-auto h-3.5 w-3.5 opacity-50" />
          : <ChevronRight className="ml-auto h-3.5 w-3.5 opacity-50" />}
      </SidebarMenuButton>
      {open && item.children && (
        <SidebarMenuSub>
          {item.children.map((child, _i, siblings) => {
            if (child.children) {
              return <AdminSubGroup key={child.label} item={child} />;
            }
            const isActive = isChildActive(child.href!, siblings, location);
            const childBadge = childBadges?.[child.href!];
            if (siblings.some((s) => s.children)) {
              const ChildIcon = child.icon;
              return (
                <SidebarMenuItem key={child.href}>
                  <SidebarMenuButton asChild isActive={isActive} tooltip={child.label}>
                    <NavLink to={child.href!} onClick={closeMobileNav}>
                      <ChildIcon />
                      <span>{child.label}</span>
                      <NavBadge count={childBadge} />
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              );
            }
            return (
              <React.Fragment key={child.href}>
                <SidebarMenuSubItem>
                  <SidebarMenuSubButton asChild isActive={isActive} size="sm">
                    <NavLink to={child.href!} onClick={closeMobileNav}>
                      <span className="truncate">{child.label}</span>
                      <NavBadge count={childBadge} />
                    </NavLink>
                  </SidebarMenuSubButton>
                </SidebarMenuSubItem>
                {childAddons?.[child.href!]}
              </React.Fragment>
            );
          })}
        </SidebarMenuSub>
      )}
    </SidebarMenuItem>
  );
}

// ─── Collapsible admin sub-group ─────────────────────────────────────────────

function AdminSubGroup({ item }: { item: NavItemDef }) {
  const location = useLocation();
  const navigate = useNavigate();
  const closeMobileNav = useCloseMobileNav();
  const { state: sidebarState, isMobile } = useSidebar();
  // `state` derives from the persisted *desktop* open flag and never from `openMobile`,
  // so without the `isMobile` term a collapsed rail leaks into the mobile drawer. That
  // is reachable: a phone in landscape is >=768px and renders the desktop rail with its
  // collapse chevron, so collapsing there and rotating back gives the drawer the
  // icon-rail branch — whose popovers portal to `document.body`, outside the sheet and
  // therefore pointer-dead while it is open, while the `group-data-[collapsible=icon]`
  // classes do not apply so the labels still show. A half-collapsed state that exists
  // nowhere in the design. The primitive itself already guards this way (`sidebar.tsx`
  // hides the rail's own rail handle on `state !== 'collapsed' || isMobile`).
  const isCollapsed = sidebarState === 'collapsed' && !isMobile;
  const childActive = isDescendantActive(item, location.pathname, location.search);
  const [expanded, setExpanded] = useState(childActive);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const triggerRef = React.useRef<HTMLDivElement>(null);
  const popoverRef = React.useRef<HTMLDivElement>(null);
  const [popoverPos, setPopoverPos] = useState({ top: 0, left: 0 });
  const Icon = item.icon;

  // Close popover on outside click
  React.useEffect(() => {
    if (!popoverOpen) return;
    function handler(e: MouseEvent) {
      if (
        popoverRef.current && !popoverRef.current.contains(e.target as Node) &&
        triggerRef.current && !triggerRef.current.contains(e.target as Node)
      ) {
        setPopoverOpen(false);
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [popoverOpen]);

  // Close popover when sidebar expands
  React.useEffect(() => {
    if (!isCollapsed) setPopoverOpen(false);
  }, [isCollapsed]);

  function renderChildren(children: NavItemDef[]) {
    return children.map((child) => {
      if (child.children) {
        return (
          <div key={child.label} className="py-1">
            <p className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-white/40">{child.label}</p>
            {renderChildren(child.children)}
          </div>
        );
      }
      const isActive = isChildActive(child.href!, children, location);
      return (
        <button
          key={child.href}
          onClick={() => { closeMobileNav(); navigate(child.href!); setPopoverOpen(false); }}
          className={cn(
            'flex w-full items-center gap-2 rounded px-3 py-1.5 text-sm transition-colors',
            isActive
              ? 'bg-white/20 text-white font-medium'
              : 'text-white/70 hover:bg-white/10 hover:text-white',
          )}
        >
          <child.icon className="h-3.5 w-3.5 flex-shrink-0" />
          {child.label}
        </button>
      );
    });
  }

  // Collapsed: show popover flyout via portal
  if (isCollapsed) {
    return (
      <SidebarMenuItem>
        <div ref={triggerRef}>
          <SidebarMenuButton
            isActive={childActive}
            tooltip={popoverOpen ? undefined : item.label}
            onClick={() => {
              // Groups with their own landing page (e.g. Ads Center, Campaigns)
              // navigate there in addition to showing the children popover.
              if (item.href) navigate(item.href);
              if (!popoverOpen && triggerRef.current) {
                const rect = triggerRef.current.getBoundingClientRect();
                setPopoverPos({ top: rect.top, left: rect.right + 4 });
              }
              setPopoverOpen(o => !o);
            }}
          >
            <Icon />
            <span>{item.label}</span>
          </SidebarMenuButton>
        </div>
        {popoverOpen && item.children && createPortal(
          <div
            ref={popoverRef}
            className="fixed z-50 min-w-[180px] max-h-[70vh] overflow-y-auto rounded-md border border-slate-700 bg-[hsl(222,47%,16%)] p-1.5 shadow-xl"
            style={{ top: popoverPos.top, left: popoverPos.left }}
          >
            <p className="px-3 py-1.5 text-xs font-semibold text-white/60">{item.label}</p>
            {renderChildren(item.children)}
          </div>,
          document.body,
        )}
      </SidebarMenuItem>
    );
  }

  // Expanded: collapsible; groups with their own landing page (Ads Center,
  // Campaigns) also navigate there on click.
  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        isActive={childActive}
        onClick={() => {
          // Mirror top-level section groups: navigate to the group's landing
          // page (when it has one) and toggle the submenu.
          if (item.href) navigate(item.href);
          setExpanded(e => !e);
        }}
        tooltip={item.label}
      >
        <Icon />
        <span className="truncate">{item.label}</span>
        {expanded ? <ChevronDown className="ml-auto h-3.5 w-3.5 opacity-50" /> : <ChevronRight className="ml-auto h-3.5 w-3.5 opacity-50" />}
      </SidebarMenuButton>
      {expanded && item.children && (
        <SidebarMenuSub>
          {item.children.map((child, _i, siblings) => {
            if (child.children) {
              return <AdminSubGroup key={child.label} item={child} />;
            }
            const isActive = isChildActive(child.href!, siblings, location);
            return (
              <SidebarMenuSubItem key={child.href}>
                <SidebarMenuSubButton asChild isActive={isActive} size="sm">
                  <NavLink to={child.href!} onClick={closeMobileNav}>{child.label}</NavLink>
                </SidebarMenuSubButton>
              </SidebarMenuSubItem>
            );
          })}
        </SidebarMenuSub>
      )}
    </SidebarMenuItem>
  );
}

// ─── Collapse toggle ─────────────────────────────────────────────────────────

function SidebarCollapseToggle() {
  const { state, toggleSidebar } = useSidebar();
  return (
    <CollapseChevron
      collapsed={state === 'collapsed'}
      onClick={toggleSidebar}
      side="left"
    />
  );
}

// ─── Sidebar ─────────────────────────────────────────────────────────────────

export function Sidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  // Desktop icon rail only. `state` follows the persisted desktop flag even on a
  // phone, so the `isMobile` term keeps rail counts out of the drawer (see the
  // matching guard in NavGroupItem).
  const { state: sidebarState, isMobile } = useSidebar();
  const railCollapsed = sidebarState === 'collapsed' && !isMobile;
  // Dismisses the mobile drawer once a tap has opened a screen; a no-op above
  // `md`, so nothing here changes on desktop.
  const closeMobileNav = useCloseMobileNav();
  const user = useAuthStore((s: AuthStore) => s.user);
  const accountId = useAuthStore((s: AuthStore) => s.accountId);

  const { data: navData } = useQuery({
    queryKey: ['navigation', 'main', accountId],
    queryFn: getNavigation,
    enabled: !!accountId,
    staleTime: Infinity,
    retry: false,
  });

  // Scope the counts to every facility selected in the global picker so they
  // mirror what the Communication Center list shows under that scope. One
  // facility sends the same `facilityId` request as before; none sends nothing.
  const selectedFacilityIds = useGlobalFacilityStore((s) => s.selectedFacilityIds);
  const facilityKey = facilityScopeKey(selectedFacilityIds);

  const { data: badgeData } = useQuery({
    queryKey: ['navigation', 'badges', accountId, facilityKey],
    queryFn: () => getNavigationBadges(selectedFacilityIds),
    enabled: !!accountId,
    retry: false,
    staleTime: 60_000,
  });

  const branding: AccountBranding | undefined = navData?.branding;

  const logoSubText = (branding?.logoSubText ?? '').trim();

  // The caption below is sized to the logo's rendered width, which is 0 until
  // the image has loaded — and a 0px-wide caption breaks one character per
  // line and reads as a vertical strip. So it waits for the logo. If the logo
  // fails to load there is no width to inherit and the caption sizes itself.
  const logo = useImageLoadState(branding?.logo);

  // The caption is drawn as wide as the logo so its alignment is relative to the
  // logo's edges — see the w-0 min-w-full note on the element itself. The logo is
  // drawn at its intrinsic aspect (self-center, w-auto) rather than stretched
  // across the header, which is what makes the wrapper hug it.
  const logoSubTextStyle = useMemo<React.CSSProperties>(() => ({
    fontSize: `${logoSubTextFontSize(Number(branding?.logoSubTextSize ?? 0))}px`,
    letterSpacing: logoSubTextLetterSpacing(branding?.logoSubTextLetterSpacing),
    textAlign: branding?.logoSubTextAlign ?? 'center',
    fontFamily: logoSubTextFontStack(branding?.logoSubTextFont),
    // Unset colour leaves the text-white/70 class in charge; an inline colour
    // overrides it.
    color: logoSubTextColor(branding?.logoSubTextColor),
    // Relative rather than a margin, so moving the caption up over the logo
    // does not change the header's height or shift the nav below it.
    position: 'relative',
    top: `${logoSubTextOffsetY(Number(branding?.logoSubTextOffsetY ?? 0))}px`,
  }), [
    branding?.logoSubTextSize,
    branding?.logoSubTextLetterSpacing,
    branding?.logoSubTextAlign,
    branding?.logoSubTextFont,
    branding?.logoSubTextColor,
    branding?.logoSubTextOffsetY,
  ]);

  const sidebarStyle = useMemo<React.CSSProperties>(() => {
    if (!branding?.bgColor) return DARK_SIDEBAR_VARS;
    const hex = branding.bgColor.startsWith('#') ? branding.bgColor : `#${branding.bgColor}`;
    return { ...DARK_SIDEBAR_VARS, '--sidebar': hexToHslString(hex) } as React.CSSProperties;
  }, [branding?.bgColor]);

  useEffect(() => {
    applyBrandingColors(branding?.buttonColor);
  }, [branding?.buttonColor]);

  // Pre-badges-endpoint source for the messages count; leafBadgeFor() falls
  // back to it when /navigation/badges has not answered yet.
  const legacyMessagesBadge = navData?.items?.find((i: NavItem) => i.key === 'messages')?.badge;

  const { data: inboxCounts } = useQuery({
    queryKey: ['inbox-counts'],
    queryFn: () => getInboxCounts(),
    enabled: !!accountId,
    retry: false,
    staleTime: 60_000,
  });

  // Same queries as the Dashboard's Pending Tasks and Alerts cards. The Alerts
  // number matches its card; the Tasks number deliberately does not (below).
  const { data: myTaskCounts } = useMyTaskCounts({ enabled: !!accountId });
  const tasksDueNow = myTaskCounts ? myTaskCounts.pastDue + myTaskCounts.dueToday : undefined;
  const { data: unreadAlerts } = useUnreadAlertCount({ enabled: !!accountId });

  const userName = user?.displayName ?? (user ? `${user.firstName} ${user.lastName}`.trim() : 'Staff');
  // Prefer the account's customized roleTitle from aa_accounts_roles; fall
  // back to the raw users.role; render the line blank when the user has no
  // role assigned (no more synthetic "userLevel → label" mapping).
  const userRole = user?.roleTitle ?? user?.role ?? navData?.user?.roleTitle ?? navData?.user?.role ?? '';
  const userAvatarUrl = user?.avatarUrl ?? navData?.user?.avatarUrl ?? null;
  const userPermissions = user?.permissions ?? []; // QA badge visibility below
  // User-level + permission + per-account module filtering, all in
  // useNavAccess (shared with RequireModule's safe-landing redirect).
  const filteredNavItems = useFilteredNavItems();

  function handleLogout() {
    // Everything goes through endSession now: it POSTs /auth/logout so the token
    // is actually revoked server-side (nothing did that before), clears the query
    // cache, and lets SessionWatcher navigate only after React has unmounted the
    // PHI-bearing tree — which is what stops the browser's "Leave site?" prompt
    // firing when you sign out with an unsaved note open.
    endSession('user', true);
  }

  return (
    <SidebarPrimitive
      collapsible="icon"
      side="left"
      className="text-white border-r-0"
      style={sidebarStyle}
    >
      {/* Logo / branding */}
      <SidebarHeader className="py-5 px-4">
        {branding?.logo ? (
          <div className="self-center max-w-full group-data-[collapsible=icon]:hidden">
            <img
              ref={logo.ref}
              src={branding.logo}
              alt={branding.name ?? ''}
              className="block h-10 w-auto max-w-full object-contain"
              onLoad={logo.onLoad}
              onError={logo.onError}
            />
            {/* Caption the account types in under the site logo, replacing the
                name that used to be baked into the image. Blank = nothing shown. */}
            {/* w-0 min-w-full makes this exactly as wide as the wrapper while
                contributing nothing to the wrapper's own shrink-to-fit width, so
                the wrapper is sized by the logo alone and the caption inherits
                that width. That puts left/right justify on the logo's edges
                structurally, with nothing measured. */}
            {/* Not rendered until the logo has a width to hand down; a broken
                logo gets a caption that sizes itself instead. */}
            {logoSubText && logo.state !== 'loading' ? (
              <p
                className={cn(
                  'mt-0.5 leading-tight text-white/70',
                  logo.state === 'loaded' ? 'w-0 min-w-full break-words' : 'whitespace-nowrap',
                )}
                style={logoSubTextStyle}
              >
                {logoSubText}
              </p>
            ) : null}
          </div>
        ) : (
          <div className="flex items-center gap-2 group-data-[collapsible=icon]:justify-center">
            <HeartPulse className="h-6 w-6 text-white flex-shrink-0" />
            <span className="text-sm font-semibold text-white tracking-tight group-data-[collapsible=icon]:hidden">
              {branding?.name || 'Prospera AI'}
            </span>
          </div>
        )}
        {branding?.logoSquare && (
          <img
            src={branding.logoSquare}
            alt={branding.name ?? ''}
            className={cn(
              'h-8 w-8 rounded-lg object-contain flex-shrink-0',
              branding?.logo ? 'hidden group-data-[collapsible=icon]:block' : '',
            )}
          />
        )}
        {/* Same caption for an account with no full site logo — there is no logo
            width to size it against, so it keeps the fixed fallback size. */}
        {logoSubText && !branding?.logo ? (
          <p
            className="-mt-1 leading-tight text-white/70 group-data-[collapsible=icon]:hidden"
            style={logoSubTextStyle}
          >
            {logoSubText}
          </p>
        ) : null}
      </SidebarHeader>

      {/* Search */}
      <SidebarGroup className="group-data-[collapsible=icon]:hidden px-3 pb-3 pt-0">
        <SidebarPatientSearch onNavigate={closeMobileNav} />
      </SidebarGroup>

      {/* Global facility selector */}
      <SidebarGroup className="group-data-[collapsible=icon]:hidden px-3 pb-3 pt-0">
        <GlobalFacilitySelector />
      </SidebarGroup>

      {/* Navigation items */}
      <SidebarContent>
        <SidebarGroup>
          <SidebarMenu>
            {!navData && !user ? (
              Array.from({ length: 8 }).map((_, i) => (
                <SidebarMenuItem key={i}>
                  <SidebarMenuSkeleton showIcon />
                </SidebarMenuItem>
              ))
            ) : (
              filteredNavItems.map((item) => {
                if (item.children) {
                  if (!item.href) return <AdminSubGroup key={item.label} item={item} />;

                  // Parent rows carry a count for the child that drives their
                  // attention signal: Dashboard mirrors Inbox unread, CRM shows
                  // new leads, Tasks shows tasks due now (past due + due today),
                  // Communication Center shows unread conversations (its Mentions
                  // child counts separately). See groupBadgesFor.
                  const groupBadges = groupBadgesFor(item.href, {
                    badges: badgeData,
                    inboxUnread: inboxCounts?.unread,
                    legacyMessages: legacyMessagesBadge,
                    permissions: userPermissions,
                    tasksDueNow,
                  });

                  // The mentioned conversations listed under Mentions.
                  const childAddons = item.href === COMM_CENTER_NAV_HREF
                    ? {
                      [MENTIONS_NAV_HREF]: (
                        <MentionsPreview
                          count={groupBadges.children[MENTIONS_NAV_HREF]}
                          facilityIds={selectedFacilityIds}
                          facilityKey={facilityKey}
                          accountId={accountId}
                        />
                      ),
                    }
                    : undefined;

                  return (
                    <NavGroupItem
                      key={item.label}
                      item={item}
                      badge={groupBadges.parent}
                      childBadges={groupBadges.children}
                      childAddons={childAddons}
                    />
                  );
                }

                if (item.external) {
                  return (
                    <SidebarMenuItem key={item.label}>
                      <SidebarMenuButton
                        tooltip={item.label}
                        // Closes the drawer too. This one opens a new tab rather than a
                        // route, so leaving it open is worse than for an in-app leaf:
                        // the user comes back to the original tab and finds the nav
                        // still covering the page they left. No nav item sets
                        // `external` today, so this is correctness ahead of a caller.
                        onClick={() => {
                          closeMobileNav();
                          window.open(item.href!, '_blank', 'noopener,noreferrer');
                        }}
                      >
                        <item.icon />
                        <span>{item.label}</span>
                        <ExternalLink className="ml-auto h-3 w-3 opacity-40" />
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );
                }

                const hrefUrl = new URL(item.href!, 'http://x');
                // A leaf configured without a query string owns its whole
                // pathname, so it stays highlighted whatever filters the page
                // itself has put in the URL — otherwise Labs would go dark the
                // moment the user cleared the filter it linked them to.
                const isActive = hrefUrl.search
                  ? location.pathname === hrefUrl.pathname && location.search === hrefUrl.search
                  : location.pathname === hrefUrl.pathname;

                // Labs points at its unmatched results while any are waiting: a
                // count the user cannot act on is just noise.
                const target = item.href === '/lab-orders' ? labsNavHref(badgeData?.labs) : item.href!;

                const badge = leafBadgeFor(item.href, badgeData, userPermissions, {
                  messagesFallback: legacyMessagesBadge,
                  alerts: unreadAlerts,
                });

                return (
                  <SidebarMenuItem key={item.label}>
                    <SidebarMenuButton
                      isActive={isActive}
                      tooltip={item.label}
                      onClick={() => { closeMobileNav(); navigate(target); }}
                    >
                      <item.icon />
                      {/* `truncate` is explicit because the badge that follows
                          takes this span out of `[&>span:last-child]`, which is
                          where the button's truncation used to come from. */}
                      <span className="truncate">{item.label}</span>
                      {railCollapsed
                        ? <NavRailCountLabel count={badge} />
                        : <NavBadge count={badge} />}
                    </SidebarMenuButton>
                    {railCollapsed && <NavRailBadge count={badge} />}
                  </SidebarMenuItem>
                );
              })
            )}
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>

      {/* User footer — pill opens UserPillMenu (timezone switcher + Settings + Sign Out).
          UserPillMenu owns its own <button> trigger directly to avoid the
          asChild forwarding fragility we hit when wrapping SidebarMenuButton.
          Quick-access LogOut icon stays in place so signing out doesn't
          require opening the popover. */}
      <SidebarFooter className="border-t border-white/10">
        <SidebarMenu>
          <SidebarMenuItem>
            <UserPillMenu
              userName={userName}
              userRole={userRole}
              userAvatarUrl={userAvatarUrl}
              onSignOut={handleLogout}
              onNavigate={closeMobileNav}
            />
            <button
              className="absolute right-2 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/70 transition-colors group-data-[collapsible=icon]:hidden"
              onClick={handleLogout}
              title="Sign out"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>

      <SidebarCollapseToggle />
    </SidebarPrimitive>
  );
}
